/**
 * Client-side Supabase queries for event corrections.
 * Follows the same pattern as db-keywords.ts.
 */

import { getSupabase } from './supabase';

export type IssueType = 'wrong_date' | 'missing_event' | 'wrong_event_name' | 'wrong_details' | 'duplicate_event' | 'missing_translation' | 'broken_link' | 'other';
export type CorrectionStatus = 'pending' | 'reviewed' | 'applied' | 'dismissed';

export interface EventCorrection {
  id: string;
  event_slug: string;
  event_id: string | null;
  event_title: string | null;
  event_country: string | null;
  issue_type: IssueType;
  correct_value: string | null;
  description: string | null;
  submitter_email: string | null;
  page_url: string | null;
  status: CorrectionStatus;
  admin_notes: string | null;
  applied_field: string | null;
  applied_value: string | null;
  contributor_name: string | null;
  contributor_city: string | null;
  field_name: string | null;
  is_duplicate: boolean;
  attachment_url: string | null;
  created_at: string;
  reviewed_at: string | null;
}

export interface CorrectionStats {
  total: number;
  pending: number;
  reviewed: number;
  applied: number;
  dismissed: number;
}

export interface SubmitCorrectionData {
  event_slug: string;
  event_id?: string;
  event_title?: string;
  event_country?: string;
  issue_type: IssueType;
  correct_value?: string;
  description?: string;
  submitter_email?: string;
  page_url?: string;
  attachment_url?: string;
}

export const ATTACHMENT_MAX_SIZE = 5 * 1024 * 1024; // 5 MB
export const ATTACHMENT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export async function uploadCorrectionAttachment(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/corrections/upload', { method: 'POST', body: form });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data.url;
}

const sb = getSupabase()!;

export async function submitCorrection(data: SubmitCorrectionData): Promise<EventCorrection> {
  const { data: row, error } = await sb
    .from('event_corrections')
    .insert(data)
    .select()
    .single();

  if (error) throw new Error(`Failed to submit correction: ${error.message}`);
  return row as unknown as EventCorrection;
}

export async function getCorrections(status?: CorrectionStatus): Promise<EventCorrection[]> {
  let query = sb.from('event_corrections').select('*');
  if (status) query = query.eq('status', status);
  query = query.order('created_at', { ascending: false });

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch corrections: ${error.message}`);
  return (data ?? []) as unknown as EventCorrection[];
}

export async function getCorrectionStats(): Promise<CorrectionStats> {
  const { data, error } = await sb.from('event_corrections').select('status');
  if (error) throw new Error(`Failed to fetch correction stats: ${error.message}`);

  const rows = (data ?? []) as Array<{ status: string }>;
  return {
    total: rows.length,
    pending: rows.filter((r) => r.status === 'pending').length,
    reviewed: rows.filter((r) => r.status === 'reviewed').length,
    applied: rows.filter((r) => r.status === 'applied').length,
    dismissed: rows.filter((r) => r.status === 'dismissed').length,
  };
}

export async function updateCorrection(
  id: string,
  updates: { status?: CorrectionStatus; admin_notes?: string; field_name?: string; is_duplicate?: boolean },
): Promise<EventCorrection> {
  const payload: Record<string, unknown> = { ...updates };
  if (updates.status && updates.status !== 'pending') {
    payload.reviewed_at = new Date().toISOString();
  }

  const { data, error } = await sb
    .from('event_corrections')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update correction: ${error.message}`);
  return data as unknown as EventCorrection;
}
